/**
 * Performance reviews (piece 34, *The employee file*; decision 3185; T2073):
 * a work-anniversary reminder and a signed file, and no review form and no
 * ratings. 🔒 Before each anniversary of a job's start the manager receives a
 * To-Do to hold the review and file it signed; the signed copy files on the job
 * under the HR document class and lists in the Employee page's Reviews section.
 */

/** 🔒 The seeded, system-assigned type a signed review files under, of the HR class. */
export const PERFORMANCE_REVIEW_FILE_TYPE_NAME = 'Performance Review';

/** 🔒 The To-Do each anniversary raises on the manager, closed when a signed review is filed on the job (piece 9's kinds). */
export const PERFORMANCE_REVIEW_DUE_KIND = 'PerformanceReviewDue';
/** The record the To-Do names: the job whose anniversary it is. */
export const PERFORMANCE_REVIEW_DUE_SOURCE = 'EmploymentRecord';

/** The report line the daily run writes, beside the reminders job's own (decision 2540's shape). */
export const RAISE_REVIEW_REMINDERS_JOB = 'raiseReviewReminders';

/** 🔒 How many days before the anniversary the To-Do reaches the manager. */
export const REVIEW_REMINDER_LEAD_DAYS = 30;

/** Filing a signed review on one job, relative to the API root. */
export function employeeReviewsPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/reviews`;
}

/** 🔒 The refusal of a filing to anybody but HR with the hire permission or a manager above the job, in a sentence. */
export const REVIEW_FILE_REFUSAL = 'A signed review is filed by HR or by a manager above this person, and this identity is neither.';

/** 🔒 An ended job takes no new review. */
export const REVIEW_ENDED_REFUSAL = 'This job has ended, so no new review is filed on it.';

/** What the Reviews section carries beside its rows. */
export interface ReviewsSection {
  /** 🔒 HR with the hire permission and a manager in the chain file a signed review on an open job. */
  mayFile: boolean;
}

/** One calendar day as `YYYY-MM-DD`, read and written in UTC so a zone never moves it. */
function dayOf(year: number, month: number, date: number): string {
  // 🔨 A start on 29 February keeps its anniversary on 28 February in a common year.
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(date, last))).toISOString().slice(0, 10);
}

/** The days from one calendar day to another, negative where the second is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000);
}

/** The calendar day a number of days after another. */
export function dayAfter(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** 🔒 The job's next work anniversary on or after today, never the start day itself, with the years it completes. */
export function nextWorkAnniversary(startDay: string, today: string): { on: string; years: number } {
  const [year, month, date] = startDay.split('-').map(Number) as [number, number, number];
  const thisYear = Number(today.slice(0, 4));
  for (let at = Math.max(thisYear, year + 1); ; at += 1) {
    const on = dayOf(at, month - 1, date);
    if (on >= today) return { on, years: at - year };
  }
}

/**
 * 🔒 The anniversary a reminder is due for today, or null: due from the lead
 * day before it until the day itself, so a run missed for a night still raises.
 */
export function reviewReminderDue(startDay: string, today: string, leadDays: number = REVIEW_REMINDER_LEAD_DAYS): { on: string; years: number; windowOpens: string } | null {
  if (startDay > today) return null;
  const next = nextWorkAnniversary(startDay, today);
  const left = daysBetween(today, next.on);
  if (left < 0 || left > leadDays) return null;
  return { ...next, windowOpens: dayAfter(next.on, -leadDays) };
}
